import { ref } from 'vue'

/**
 * Zona horaria en la que se muestran las fechas que vienen del backend.
 *
 * La fuente es `generals` flag 13 ("ZONA HORARIA"). `useAppSettingsStore` la
 * pide a `GET /hub/api/general/timezone` tras el login y la inyecta aca con
 * `setAppTimeZone`; este modulo no consulta la API a proposito, asi se puede
 * usar desde helpers y servicios sin arrastrar Pinia.
 *
 * Antes era `Etc/GMT+5` fija, asi que una instalacion fuera de Colombia veia
 * todas las fechas corridas.
 */
export const FALLBACK_TIME_ZONE = 'America/Bogota'

// `ref` y no una variable suelta: las fechas ya pintadas se recalculan solas
// cuando la zona llega desde la API.
const appTimeZone = ref(FALLBACK_TIME_ZONE)

type AppDateTimeFormat = 'date' | 'time' | 'datetime' | 'datetimeSeconds'

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const HAS_TIME_PATTERN = /(?:T|\s)\d{2}:\d{2}/
const HAS_TIME_ZONE_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/

export const isSupportedTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value })
    return true
  } catch {
    return false
  }
}

export const getAppTimeZone = (): string => appTimeZone.value

/** Aplica la zona configurada; un valor vacio o invalido conserva la vigente. */
export const setAppTimeZone = (value: string | null | undefined): string => {
  const timeZone = (value ?? '').trim()

  if (!timeZone || !isSupportedTimeZone(timeZone)) {
    return appTimeZone.value
  }

  appTimeZone.value = timeZone

  return appTimeZone.value
}

const normalizeDateTimeInput = (value: string) => {
  const trimmedValue = value.trim()
  if (!trimmedValue) return ''
  if (DATE_ONLY_PATTERN.test(trimmedValue)) return trimmedValue

  const normalizedValue = trimmedValue.replace(' ', 'T')
  if (HAS_TIME_PATTERN.test(normalizedValue) && !HAS_TIME_ZONE_PATTERN.test(normalizedValue)) {
    return `${normalizedValue}Z`
  }

  return normalizedValue
}

const getFormatterOptions = (
  format: AppDateTimeFormat,
  timeZone: string,
): Intl.DateTimeFormatOptions => {
  const baseOptions: Intl.DateTimeFormatOptions = { timeZone, hour12: false }

  if (format === 'time') {
    return { ...baseOptions, hour: '2-digit', minute: '2-digit' }
  }

  if (format === 'datetime' || format === 'datetimeSeconds') {
    return {
      ...baseOptions,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...(format === 'datetimeSeconds' ? { second: '2-digit' } : {}),
    }
  }

  return { ...baseOptions, year: 'numeric', month: '2-digit', day: '2-digit' }
}

export const parseServerDate = (value: string | number | Date | null | undefined): Date => {
  if (value == null || value === '') return new Date(NaN)

  return value instanceof Date
    ? value
    : new Date(typeof value === 'string' ? normalizeDateTimeInput(value) : value)
}

/**
 * Formatea un instante del backend (UTC) en la zona configurada.
 *
 * Las fechas sin hora (`YYYY-MM-DD`) se devuelven tal cual: convertirlas las
 * correria un dia.
 */
export const formatInAppTimeZone = (
  value: string | number | Date | null | undefined,
  format: AppDateTimeFormat = 'datetime',
  locale = 'es-CO',
) => {
  if (value == null || value === '') return '-'

  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim()) && format !== 'time') {
    const [year, month, day] = value.trim().split('-')
    return `${day}/${month}/${year}`
  }

  const date = parseServerDate(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return new Intl.DateTimeFormat(locale, getFormatterOptions(format, getAppTimeZone())).format(date)
}
